import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

const statusColors = {
  active: '#10B981', inactive: '#F59E0B', completed: '#6B7280',
  open: '#3B82F6', in_progress: '#F59E0B', cancelled: '#EF4444',
};

const SIDEBAR_WIDTH = 260;

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setShowResults(false);
    setSearchQuery('');
    setSearchResults(null);
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value) => {
    setSearchQuery(value);
    clearTimeout(searchTimeout.current);
    if (value.length < 2) {
      setSearchResults(null);
      setShowResults(false);
      return;
    }
    setSearching(true);
    setShowResults(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await api.get('/search', { params: { q: value } });
        setSearchResults(res.data);
      } catch (error) {
        console.error(error);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleLogout = () => setShowLogoutModal(true);

  const confirmLogout = async () => {
    setShowLogoutModal(false);
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;
  const isActiveGroup = (path) => location.pathname.startsWith(path);

  const navLinkStyle = (active, size = 'normal') => ({
    display: 'flex',
    alignItems: 'center',
    padding: size === 'small' ? '8px 14px' : '10px 14px',
    borderRadius: '8px',
    textDecoration: 'none',
    marginBottom: '3px',
    backgroundColor: active ? 'rgba(255,255,255,0.18)' : 'transparent',
    color: active ? '#fff' : size === 'small' ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.78)',
    fontWeight: active ? '600' : '400',
    fontSize: size === 'small' ? '13px' : '14px',
    transition: 'background-color 0.15s, color 0.15s',
  });

  const hasResults = searchResults && (
    searchResults.clients?.length > 0 ||
    searchResults.case_files?.length > 0 ||
    searchResults.budgets?.length > 0
  );
  const noResults = searchResults && !hasResults;

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <span style={{ fontSize: '18px', fontWeight: '800', color: '#fff', letterSpacing: '-0.5px' }}>Lex Manager</span>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>Sistema Notarial</div>
        </div>
        {isMobile && (
          <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', padding: 4 }}>
            <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Menú */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>

        {/* Principal */}
        <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', padding: '4px 8px', marginBottom: '6px' }}>
          Principal
        </div>
        <Link to="/dashboard" style={navLinkStyle(isActive('/dashboard'))}>
          <svg style={{ width: 18, height: 18, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Dashboard
        </Link>
        {(user?.role === 'notary' || user?.role === 'supervisor') && (
          <Link to="/notary-dashboard" style={navLinkStyle(isActiveGroup('/notary-dashboard'))}>
            <svg style={{ width: 18, height: 18, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Dashboard Ejecutivo
          </Link>
        )}
        <Link to="/clients" style={navLinkStyle(isActiveGroup('/clients'))}>
          <svg style={{ width: 18, height: 18, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Clientes
        </Link>
        <Link to="/case-files" style={navLinkStyle(isActiveGroup('/case-files'))}>
          <svg style={{ width: 18, height: 18, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          Expedientes
        </Link>

        {/* Presupuestos */}
        <Link to="/budgets" style={navLinkStyle(isActiveGroup('/budgets'))}>
          <svg style={{ width: 18, height: 18, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          Presupuestos
        </Link>

        {/* Cuentas por Cobrar */}
        <Link to="/accounts-receivable" style={navLinkStyle(isActiveGroup('/accounts-receivable'))}>
          <svg style={{ width: 18, height: 18, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Cuentas por Cobrar
        </Link>

         {/* Agenda */}
       <Link to="/agenda" style={navLinkStyle(isActiveGroup('/agenda'))}>
          <svg style={{ width: 18, height: 18, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Agenda
        </Link>


        {/* Manual de Usuario */}
        <Link to="/manual" style={navLinkStyle(isActive('/manual'))}>
        <svg style={{ width: 18, height: 18, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
        Manual de Usuario
        </Link>

        <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)', margin: '10px 0' }}></div>

        {/* Configuración — solo notario */}
        {user?.role === 'notary' && (
          <>
            <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', padding: '4px 8px', marginBottom: '6px' }}>
              Configuración
            </div>
            <Link to="/settings/users" style={navLinkStyle(isActive('/settings/users'), 'small')}>
              <svg style={{ width: 16, height: 16, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Usuarios
            </Link>
            <Link to="/settings/activity-types" style={navLinkStyle(isActive('/settings/activity-types'), 'small')}>
              <svg style={{ width: 16, height: 16, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Tipos de Actividad
            </Link>
            <Link to="/settings/procedure-types" style={navLinkStyle(isActive('/settings/procedure-types'), 'small')}>
              <svg style={{ width: 16, height: 16, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Tipos de Trámite
            </Link>
            <Link to="/settings/system" style={navLinkStyle(isActive('/settings/system'), 'small')}>
              <svg style={{ width: 16, height: 16, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Config. General
            </Link>
            <Link to="/audit-log" style={navLinkStyle(isActive('/audit-log'), 'small')}>
              <svg style={{ width: 16, height: 16, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Log de Auditoría
            </Link>

            <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)', margin: '10px 0' }}></div>

            <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', padding: '4px 8px', marginBottom: '6px' }}>
              Presupuestos
            </div>
            <Link to="/settings/notary" style={navLinkStyle(isActive('/settings/notary'), 'small')}>
              <svg style={{ width: 16, height: 16, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Datos de la Notaría
            </Link>
            <Link to="/settings/budget-document-types" style={navLinkStyle(isActiveGroup('/settings/budget-document-types'), 'small')}>
              <svg style={{ width: 16, height: 16, marginRight: 10, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Tipos de Documento
            </Link>
          </>
        )}
      </div>

      {/* Footer sidebar */}
      <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.08)' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#fff', fontSize: '14px', flexShrink: 0, marginRight: 10 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{user?.role === 'notary' ? 'Notario' : user?.role === 'supervisor' ? 'Supervisor' : 'Staff'}</div>
          </div>
          <Link to="/profile" title="Mi perfil" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: '4px', marginRight: 4, display: 'inline-flex' }}>
            <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </Link>
          <button onClick={handleLogout} title="Cerrar sesión" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', padding: '4px' }}>
            <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Header */}
      <div style={{
        position: 'fixed', top: 0, left: isMobile ? 0 : SIDEBAR_WIDTH, right: 0, height: 56,
        backgroundColor: '#5066E1', zIndex: 1000,
        display: 'flex', alignItems: 'center', padding: '0 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        {isMobile && (
          <button onClick={() => setSidebarOpen(true)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px', marginRight: 12, display: 'flex' }}>
            <svg style={{ width: 22, height: 22 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        {isMobile && (
          <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#fff', marginRight: 12 }}>Lex Manager</span>
        )}

        {/* Búsqueda */}
        <div ref={searchRef} style={{ position: 'relative', flex: 1, maxWidth: isMobile ? '100%' : 400 }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={isMobile ? 'Buscar...' : 'Buscar clientes, expedientes...'}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => searchResults && setShowResults(true)}
              style={{
                width: '100%', padding: '6px 12px 6px 32px',
                backgroundColor: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none',
              }}
            />
            <svg style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.6)', width: 15, height: 15 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {showResults && (
            <div style={{ position: 'absolute', top: '38px', left: 0, right: 0, backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 9999, maxHeight: '70vh', overflowY: 'auto' }}>
              {searching && <div className="text-center py-3 text-muted small">Buscando...</div>}
              {!searching && noResults && <div className="text-center py-4 text-muted small">Sin resultados para "{searchQuery}"</div>}
              {!searching && hasResults && (
                <>
                  {searchResults.clients?.length > 0 && (
                    <>
                      <div style={{ padding: '8px 16px 4px', fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Clientes</div>
                      {searchResults.clients.map(client => (
                        <Link key={client.id} to={`/clients/${client.id}`} onClick={() => setShowResults(false)}
                          style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', textDecoration: 'none', color: '#111', borderBottom: '1px solid #F3F4F6' }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#EEF0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#5066E1', flexShrink: 0, marginRight: 10, fontSize: 13 }}>
                            {client.name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.name}</div>
                            <div style={{ fontSize: '12px', color: '#6B7280' }}>{client.email || client.phone || 'Sin contacto'}</div>
                          </div>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColors[client.status] || '#ccc', flexShrink: 0 }}></div>
                        </Link>
                      ))}
                    </>
                  )}
                  {searchResults.case_files?.length > 0 && (
                    <>
                      <div style={{ padding: '8px 16px 4px', fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Expedientes</div>
                      {searchResults.case_files.map(cf => (
                        <Link key={cf.id} to={`/case-files/${cf.id}`} onClick={() => setShowResults(false)}
                          style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', textDecoration: 'none', color: '#111', borderBottom: '1px solid #F3F4F6' }}>
                          <div style={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 10 }}>
                            <svg style={{ width: 15, height: 15, color: '#F59E0B' }} fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cf.title}</div>
                            <div style={{ fontSize: '12px', color: '#6B7280' }}>{cf.client_name}</div>
                          </div>
                          <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '12px', backgroundColor: '#FEE2E2', color: '#EF4444', fontWeight: '600', flexShrink: 0, marginLeft: 8 }}>
                            {cf.elapsed_time}
                          </span>
                        </Link>
                      ))}
                    </>
                  )}
                  {searchResults.budgets?.length > 0 && (
                    <>
                        <div style={{ padding: '8px 16px 4px', fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Presupuestos</div>
                        {searchResults.budgets.map(b => (
                        <Link key={b.id} to={`/budgets/${b.id}`} onClick={() => setShowResults(false)}
                            style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', textDecoration: 'none', color: '#111', borderBottom: '1px solid #F3F4F6' }}>
                            <div style={{ width: 32, height: 32, borderRadius: '8px', backgroundColor: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 10 }}>
                            <svg style={{ width: 15, height: 15, color: '#10B981' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px' }}>{b.folio}</div>
                            <div style={{ fontSize: '12px', color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {b.client_name} · {b.document_type}
                            </div>
                            </div>
                            <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '12px', backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: '600', flexShrink: 0, marginLeft: 8 }}>
                            {b.currency?.toUpperCase()}
                            </span>
                        </Link>
                        ))}
                    </>
                    )}
                </>
              )}
            </div>
          )}
        </div>

        {!isMobile && (
          <div style={{ marginLeft: 'auto', paddingLeft: 16, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#fff' }}>{user?.name}</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>{user?.role === 'notary' ? 'Notario' : user?.role === 'supervisor' ? 'Supervisor' : 'Staff'}</div>
            </div>
            <Link to="/profile" title="Mi perfil" style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff', padding: '6px 10px', textDecoration: 'none', display: 'inline-flex' }}>
              <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </Link>
            <button onClick={handleLogout} title="Cerrar sesión" style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff', padding: '6px 10px' }}>
              <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Sidebar desktop */}
      {!isMobile && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: SIDEBAR_WIDTH, height: '100vh', backgroundColor: '#3d52c4', zIndex: 999, display: 'flex', flexDirection: 'column', boxShadow: '2px 0 8px rgba(0,0,0,0.1)' }}>
          <SidebarContent />
        </div>
      )}

      {/* Sidebar móvil */}
      {isMobile && (
        <>
          {sidebarOpen && (
            <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1998 }} />
          )}
          <div style={{ position: 'fixed', top: 0, left: 0, width: '80%', maxWidth: 300, height: '100vh', backgroundColor: '#3d52c4', zIndex: 1999, display: 'flex', flexDirection: 'column', transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.25s ease', boxShadow: '2px 0 12px rgba(0,0,0,0.2)' }}>
            <SidebarContent />
          </div>
        </>
      )}

      {/* Contenido */}
      <div style={{ marginLeft: isMobile ? 0 : SIDEBAR_WIDTH, marginTop: 56, minHeight: 'calc(100vh - 56px)', backgroundColor: '#f5f5f5', padding: isMobile ? 12 : 20, overflowX: 'hidden' }}>
        {children}
      </div>

      {/* Modal logout */}
      {showLogoutModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '32px', maxWidth: '380px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg style={{ width: 26, height: 26, color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </div>
            <h5 style={{ marginBottom: '8px' }}>Cerrar sesión</h5>
            <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '24px' }}>¿Estás seguro que deseas cerrar tu sesión?</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => setShowLogoutModal(false)} style={{ padding: '8px 24px', borderRadius: '8px', border: '1px solid #D1D5DB', backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                Cancelar
              </button>
              <button onClick={confirmLogout} style={{ padding: '8px 24px', borderRadius: '8px', border: 'none', backgroundColor: '#EF4444', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        input::placeholder { color: rgba(255,255,255,0.5) !important; }
        body { background-color: #f5f5f5 !important; margin: 0; }
        * { box-sizing: border-box; }
        @media (max-width: 767px) {
          .container-fluid { padding-left: 0 !important; padding-right: 0 !important; }
        }
      `}</style>
    </>
  );
};

export default Layout;